import { Suspense } from 'react';
import { Box, MantineColor, MantineSpacing, ScrollArea } from '@mantine/core';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import { ContainerBreakpointProvider, useContainerQuery } from './ContainerBreakpoint';
import { ContentPaneOverlayProvider, ContentPaneOverlaySlot } from './ContentPaneOverlay';
import PageLoader from 'src/UI/loader/PageLoader';

type Props = {
  children: React.ReactNode;
  style?: React.CSSProperties;
  bg?: MantineColor;
  p?: MantineSpacing;
  collapsed?: boolean;
  scalePreview?: boolean;
};

export function AppContentPane({ children, style, bg, p, collapsed, scalePreview }: Props) {
  return (
    <ContentPaneOverlayProvider>
      <Box
        pos="relative"
        bg={bg ?? 'contentBackground'}
        style={{
          borderRadius:
            'var(--mantine-spacing-xxl) var(--mantine-spacing-xxl) var(--mantine-spacing-xxxl) var(--mantine-spacing-xxxl)',
          overflow: 'hidden',
          flexGrow: collapsed ? 0 : 1,
          flexShrink: 0,
          flexBasis: 0,
          opacity: collapsed ? 0 : 1,
          minWidth: 0,
          transition: 'flex-grow 0.35s ease-out, opacity 0.25s ease-out, transform 0.15s ease-out',
          transform: scalePreview ? 'scale(0.97)' : 'scale(1)',
          transformOrigin: 'center center',
          ...style,
        }}
      >
        <ContentPaneOverlaySlot />
        <ScrollArea
          h="100%"
          w="100%"
          scrollbarSize={6}
          type="scroll"
          styles={{
            root: {
              borderRadius:
                'var(--mantine-spacing-xxl) var(--mantine-spacing-xxl) var(--mantine-spacing-xxxl) var(--mantine-spacing-xxxl)',
            },
            content: {
              minHeight: '100%',
              height: '100%',
            },
            viewport: {
              overscrollBehavior: 'contain',
            },
            scrollbar: {
              background: 'transparent',
              marginTop: 'var(--mantine-spacing-xxl)',
              marginBottom: 'var(--mantine-spacing-xxxl)',
              marginRight: 2,
              padding: 0,
            },
            thumb: {
              background: 'black',
            },
          }}
        >
          <ContainerBreakpointProvider>
            <ContentPaneInner bg={bg} p={p}>
              <AppBreadcrumbs />
              <Suspense fallback={<PageLoader absolute={false} />}>{children}</Suspense>
            </ContentPaneInner>
          </ContainerBreakpointProvider>
        </ScrollArea>
      </Box>
    </ContentPaneOverlayProvider>
  );
}

function ContentPaneInner({
  children,
  bg,
  p,
}: {
  children: React.ReactNode;
  bg?: MantineColor;
  p?: MantineSpacing;
}) {
  const cq = useContainerQuery();

  return (
    <Box
      bg={bg ?? 'contentBackground'}
      px={p ?? cq({ base: 'sm', lg: 'lg', xl: 'xxl' })}
      py={p ?? cq({ base: 'sm', xl: 'lg' })}
      h="100%"
    >
      {children}
    </Box>
  );
}
